import { cx } from '../../utils/cx';

interface IYesNoToggleProps {
  /** `null` renders an unselected pair — neither option highlighted. */
  value: boolean | null;
  onChange: (value: boolean) => void;
  /** Labels the radiogroup for screen readers — pass the question text. */
  ariaLabel: string;
  className?: string;
}

/**
 * Full-width Yes/No segmented control. Selection is drawn with an outline ring
 * (not a border-width swap) so picking an option doesn't shift the box model
 * and bounce the row. Unlike {@link ButtonGroup}, the two options are gapped,
 * individually rounded cards rather than a joined segmented control.
 */
const YesNoToggle = ({ value, onChange, ariaLabel, className }: IYesNoToggleProps) => {
  const renderOption = (optionValue: boolean, label: string) => {
    const isSelected = value === optionValue;
    return (
      <button
        type="button"
        onClick={() => onChange(optionValue)}
        aria-pressed={isSelected}
        className={cx(
          'bg-primary shadow-xs text-md text-secondary flex flex-1 items-center justify-center rounded-xl px-4 py-3 font-medium outline-2 -outline-offset-2',
          isSelected ? 'outline-brand' : 'outline-secondary'
        )}
      >
        {label}
      </button>
    );
  };
  return (
    <div className={cx('flex w-full gap-2', className)} role="radiogroup" aria-label={ariaLabel}>
      {renderOption(true, 'Yes')}
      {renderOption(false, 'No')}
    </div>
  );
};
YesNoToggle.displayName = 'YesNoToggle';

export default YesNoToggle;
